import {
  getActivePinia,
  type StateTree,
  type Store,
  type Pinia,
} from 'pinia'
import { type AxiosError, type AxiosResponse } from 'axios'
import { omit } from 'lodash-es'

import { axiosInstance } from '@services/api/apiClient'
import { getApiRoute, reactiveApiUrls } from '@core/utils/api'

import {
  type AllowedRequestConfigProperties,
  type ApiRequestStoreKey,
  type ApiHydraCollectionResponse,
  type ApiCollectionStore,
  type ApiStoreKey,
  type ApiMethods,
  type ResetRequestStatusConfig,
  type SendRequestConfig,
  type CustomAxiosRequestConfig,
} from '@services/api/declarations'
import { ZodError } from 'zod'

// ***
// SET ERROR
// ***

function setError(error?: AxiosError): AxiosError | object | null {
  if (typeof error === 'string') return { error: { message: error } }
  return typeof error === 'object' ? error : null
}

// ***
// SET HEADERS
// ***

function setHeaders(requestMethod: ApiMethods, customHeaders: AllowedRequestConfigProperties['headers']) {
  // Patch requests needs special content-type header
  const patchHeader = { 'Content-Type': 'application/merge-patch+json' }

  if (requestMethod === 'PATCH') {
    if (!customHeaders || !Object.keys(customHeaders).length) {
      return { headers: patchHeader }
    }

    Object.assign(customHeaders, patchHeader)
  }

  return { headers: customHeaders }
}

// ***
// MANAGE RESPONSE
// ***

function manageResponse<ResponseShape>(
  store: Store<ApiStoreKey, Record<ApiRequestStoreKey, ResponseShape>>,
  storeKey: ApiRequestStoreKey,
  saveToStore: boolean,
  { response, error }: {
    response?: AxiosResponse<ResponseShape> | AxiosResponse<ResponseShape>[],
    error?: AxiosError
  },
) {
  const parsedResponse = () => {
    if (Array.isArray(response)) return { collection: response.map(r => r.data) }
    return response?.data
  }

  if (response) {
    const payload = {
      [storeKey]: {
        ...parsedResponse(),
      },
    }
    if (saveToStore) store.$patch(payload)
    return payload
  }

  const filteredError = setError(error)
  if (filteredError) {
    const payload = {
      [storeKey]: {
        error: (filteredError as AxiosError)?.response?.data,
      },
    }
    if (saveToStore) {
      store.$dispose()
    }
    return payload
  }
  return undefined
}

// ***
// CONVERT API DATA FROM STORE
// ***

export function convertApiDataFromStore<R>(
  store: Store<ApiStoreKey, StateTree>,
  storeKey: ApiRequestStoreKey,
  result?: Record<ApiRequestStoreKey, R>,
): { data: R | undefined, totalItems: number } {
  const state = result || store?.$state
  // @ts-expect-error look for a way to avoid this
  const raw = omit(state[storeKey], ['request', 'requestInternalState'])
  const collection = (raw as ApiCollectionStore<R>)?.collection as R

  if (Array.isArray(collection)) return { data: collection, totalItems: collection.length }

  const hydra = raw as ApiHydraCollectionResponse<R>
  if (hydra && hydra['hydra:member']) {
    return {
      data: hydra['hydra:member'] as R,
      totalItems: hydra['hydra:totalItems'],
    }
  }

  return { data: raw as R, totalItems: 0 }
}

// ***
// SEND REQUEST
// ***

export async function sendRequest<RequestConfig>(
  store: Store<ApiStoreKey, StateTree>,
  {
    endpointConfig,
    requestConfig,
    endpointName = 'main',
    saveToStore = false,
  }: SendRequestConfig<RequestConfig>,
) {
  const { storeKey } = endpointConfig
  const method = storeKey.split(/(?=[A-Z])/)[0].toUpperCase() as ApiMethods

  const url = (requestConfig?.params || requestConfig?.query)
    ? getApiRoute(endpointConfig.url, requestConfig, endpointName)
    : `${reactiveApiUrls[endpointName]}${endpointConfig.url}`

  try {
    const response = await axiosInstance.request({
      method,
      url,
      data: requestConfig?.data,
      ...setHeaders(method, requestConfig?.headers as AllowedRequestConfigProperties['headers']),
      skipToken: requestConfig?.skipToken,
      withCredentials: requestConfig?.withCredentials,
      endpointKey: endpointConfig.storeKey,
    } as CustomAxiosRequestConfig)

    return manageResponse(store, storeKey, saveToStore, { response })
  } catch (error: unknown) {
    return manageResponse(store, storeKey, saveToStore, { error: error as AxiosError })
  }
}

// ***
// RESET REQUEST STATUS
// ***

export function resetRequestStatus(
  store: Store<ApiStoreKey, StateTree>,
  storeKey: ResetRequestStatusConfig['storeKey'],
  kind: ResetRequestStatusConfig['kind'],
) {
  // @ts-expect-error look for a way to avoid this
  store.$patch({
    [storeKey]: {
      requestInternalState: {
        statusName: kind,
      },
    },
  })
}

// ***
// RESET PINIA STORES
// ***
interface ExtendedPinia extends Pinia {
  _s: Map<string, Store>;
}

export function $resetPiniaStores(): Record<'all', () => void> {
  const pinia = getActivePinia() as ExtendedPinia

  if (!pinia) {
    throw new Error('There is no stores')
  }

  const resetStores: Record<string, () => void> = {}

  // eslint-disable-next-line no-underscore-dangle
  pinia._s.forEach((store, name) => {
    if (store.$dispose) {
      resetStores[name] = () => store.$dispose()
    }
  })

  resetStores.all = () => {
    // eslint-disable-next-line no-underscore-dangle
    pinia._s.forEach((store) => { store.$dispose() })
  }
  return resetStores
}
